import React, { useEffect, useState } from "react";
import { ChefHat, Sparkles, Phone } from "lucide-react";
import api from "@/lib/api";
import { useSection } from "@/context/SiteContext";
import { telHref, resolveImageUrl } from "@/lib/site";

export default function DailySpecial() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const meta = useSection("daily_special_section");
  const contact = useSection("contact");
  const tel = telHref(contact.phone_tel, contact.phone);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/daily-special");
        setData(data);
      } catch (err) {
        console.error("Daily special load failed:", err);
        setData(null);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  if (loading) return null;
  if (!data?.item) return null;

  const { item, note, is_manual } = data;
  const image = resolveImageUrl(item.image_url);

  return (
    <section id="dagens-forslag" className="py-20 sm:py-24 bg-[var(--surface-alt)] border-y border-[var(--border)]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-10 sm:mb-14">
          <span className="text-xs uppercase tracking-[0.25em] text-[var(--primary)] font-bold inline-flex items-center gap-2">
            <Sparkles size={14} /> {meta.eyebrow || "Dagens förslag"}
          </span>
          <h2 className="font-serif-display text-4xl sm:text-5xl font-black mt-3">
            {meta.title || "Kockens rekommendation"}
          </h2>
          {meta.subtitle && (
            <p className="text-[var(--text-muted)] mt-3 max-w-2xl mx-auto">{meta.subtitle}</p>
          )}
        </div>

        <div
          data-testid="daily-special-card"
          className="bg-white border border-[var(--border)] rounded-3xl overflow-hidden shadow-xl shadow-[var(--primary)]/10 max-w-4xl mx-auto grid md:grid-cols-2"
        >
          <div className="relative bg-[var(--surface-alt)] aspect-video md:aspect-auto md:min-h-[320px]">
            {image ? (
              <img src={image} alt={item.name} className="absolute inset-0 w-full h-full object-cover" />
            ) : (
              <div className="absolute inset-0 grid place-items-center">
                <ChefHat size={80} className="text-[var(--primary)]/40" />
              </div>
            )}
            {is_manual && (
              <span className="absolute top-4 left-4 rounded-full bg-white/95 border border-[var(--border)] px-3 py-1 text-xs font-bold uppercase tracking-wider text-[var(--primary)] shadow-sm">
                Handplockad
              </span>
            )}
          </div>
          <div className="p-8 sm:p-10 flex flex-col justify-center">
            {item.category && (
              <span className="text-xs uppercase tracking-wider font-bold text-[var(--text-muted)]">
                {item.category}
              </span>
            )}
            <h3 className="font-serif-display text-3xl sm:text-4xl font-black mt-2 leading-tight">
              {item.name}
            </h3>
            {item.description && (
              <p className="text-[var(--text-muted)] mt-3 leading-relaxed">{item.description}</p>
            )}
            {note && (
              <blockquote className="mt-4 border-l-4 border-[var(--primary)] pl-4 italic text-[var(--text)]/80">
                {note}
              </blockquote>
            )}
            <div className="mt-6 flex flex-wrap items-center gap-4">
              {item.price != null && (
                <span className="font-serif-display text-3xl font-black text-[var(--primary)]">
                  {item.price} kr
                </span>
              )}
              <a
                href={tel}
                data-testid="daily-special-order-btn"
                className="inline-flex items-center gap-2 rounded-full bg-[var(--text)] hover:bg-[var(--text)]/85 text-white px-5 py-2.5 text-sm font-semibold"
              >
                <Phone size={14} /> Beställ · {contact.phone || "0660-511 30"}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
